import { computed, reactive } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import useEnvParams from '@composables/useEnvParams';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import useFacets from './useFacets';
import useOneClickSaleFilterExp from './useOneClickSaleFilterExp';

function getExcludedValues(isMcom) {
  return isMcom
    ? ['special offer', 'gift with purchase']
    : ['loyallist points offers', 'gift with purchase'];
}

function hasNonExcludedSelected(facet, excludedValues) {
  return facet.selectedValues.some((val) => !excludedValues.includes(val.toLowerCase()));
}

export default function useOneClickSaleFilter() {
  const { isMcom, enableAsyncAnalyticsTags } = useEnvParams();
  const { facets, allSelectedFacets, triggerSelectedFacetsUpdate } = useFacets();
  const { oneClickSaleExpEnabled } = useOneClickSaleFilterExp();

  const excludedValues = getExcludedValues(isMcom);

  const oneClickSaleFilterEnabled = computed(() => {
    if (!oneClickSaleExpEnabled.value) return false;
    const facet = facets.value.find((f) => f.displayName === 'SPECIAL_OFFERS');
    return facet
      ? facet.values.some((val) => !excludedValues.includes(val.value.toLowerCase()))
      : false;
  });

  function buildOneClickSaleFilter() {
    const facet = allSelectedFacets.value.find((f) => f.displayName === 'SPECIAL_OFFERS');
    const isActive = facet && facet.selectedValues.length > 0 && hasNonExcludedSelected(facet, excludedValues);

    return reactive({
      id: 'one-click-sale',
      contents: { text: isMcom ? 'On sale' : 'Sales & offers' },
      outlined: isActive,
      isSelected: isActive,
    });
  }

  function fireOneClickSaleTag() {
    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'one click sale filter',
        link_name: 'one click sale filter',
      }));
    } else {
      fireTag('link', {
        event_name: 'one click sale filter',
        link_name: 'one click sale filter',
      });
    }
  }

  function selectOneClickSaleFilter() {
    const facet = facets.value.find((f) => f.displayName === 'SPECIAL_OFFERS');
    // keep any excluded values already selected, add all non-excluded values
    const keep = facet.selectedValues.filter((val) => excludedValues.includes(val.toLowerCase()));
    const add = facet.values
      .filter((val) => !excludedValues.includes(val.value.toLowerCase()))
      .map((val) => val.value);

    facet.selectedValues = Array.from(new Set([...keep, ...add]));
    fireOneClickSaleTag();
    triggerSelectedFacetsUpdate();
  }

  function deselectOneClickSaleFilter() {
    const facet = facets.value.find((f) => f.displayName === 'SPECIAL_OFFERS');
    // keep any excluded values already selected, remove all non-excluded values
    facet.selectedValues = facet.selectedValues.filter((val) => excludedValues.includes(val.toLowerCase()));
    triggerSelectedFacetsUpdate();
  }

  return {
    oneClickSaleFilterEnabled,
    buildOneClickSaleFilter,
    selectOneClickSaleFilter,
    deselectOneClickSaleFilter,
  };
}
